import React, {type PropsWithChildren} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {ColorButton} from 'app/general/widgets/ColorPicker';
import {gt} from 'app/utils/i18n';

import type {KBThemeSerializedType} from 'sweet-types';

type Props = PropsWithChildren<
  Pick<React.HTMLProps<HTMLDivElement>, 'className'> & {
    theme: KBThemeSerializedType;
    logo: string;
    kbName: string;
  }
>;

const doNothing = (e) => e.preventDefault();
export const ThemeCard = ({
  children,
  theme,
  logo,
  kbName,
  className,
}: Props) => {
  const {
    name,
    nav_background,
    nav_text,
    logo_background,
    accent_color,
    text_color,
    breadcrumb_text,
  } = theme;

  return (
    <StyledThemeCard className={cs('card', className)}>
      <div className="card-img-top p-3">
        <StyledNav
          nav_background={nav_background}
          nav_text={nav_text}
          className={'text-truncate d-flex align-items-center'}
        >
          <StyledLogoContainer logo_background={logo_background}>
            <StyledLogo src={logo} />
          </StyledLogoContainer>

          {kbName}
        </StyledNav>
        <StyledNav
          nav_background={'#FFF'}
          nav_text={breadcrumb_text}
          className={'text-truncate d-flex align-items-center shadow-sm'}
        >
          <StyledBread color={breadcrumb_text}>
            <i className="fa fa-fw mr-sm-1 fa-home"></i>
            <span className="d-none d-sm-inline">{gt('Help')}</span>
          </StyledBread>
        </StyledNav>
        <StyledBody>
          <StyledContent>
            <StyledSearch color={accent_color} disabled />
            <StyledSearchButton background={accent_color} disabled>
              <i className="fas fa-search" />
            </StyledSearchButton>
            <StyledBread className="mt-2 d-block" color={text_color}>
              {gt('Common Help Topics')}
            </StyledBread>
            <div>
              <StyledCategory color={accent_color}>
                <i className="fas fa-graduation-cap mr-1" /> {gt('Onboarding')}
              </StyledCategory>
              <StyledCategory color={accent_color}>
                <i className="fas fas fa-headset mr-1" />{' '}
                {gt('Customer Service')}
              </StyledCategory>
            </div>
          </StyledContent>
        </StyledBody>
      </div>
      <div className="card-body">
        <h5 className="card-title">{name}</h5>
        <div className="d-flex mb-2">
          <ColorButton
            bgColor={nav_background}
            isSelected={false}
            onClick={doNothing}
            key={'nav-bg'}
          />
          <ColorButton
            bgColor={logo_background}
            isSelected={false}
            onClick={doNothing}
            key={'logo-bg'}
          />
          <ColorButton
            bgColor={nav_text}
            isSelected={false}
            key={'nav-text'}
            onClick={doNothing}
          />
          <ColorButton
            bgColor={breadcrumb_text}
            isSelected={false}
            onClick={doNothing}
            key={'breadcrumb-text'}
          />
          <ColorButton
            bgColor={accent_color}
            isSelected={false}
            onClick={doNothing}
            key={'accent-color'}
          />
          <ColorButton
            bgColor={text_color}
            isSelected={false}
            onClick={doNothing}
            key={'text-color'}
          />
        </div>
      </div>
      {children && (
        <div className="card-footer d-flex justify-content-between align-items-center">
          {children}
        </div>
      )}
    </StyledThemeCard>
  );
};

const StyledThemeCard = styled.div`
  width: 18rem;
`;

const StyledNav = styled.div<{nav_background: string; nav_text: string}>`
  background-color: ${(props) => props.nav_background};
  color: ${(props) => props.nav_text};
`;

const StyledLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const StyledLogoContainer = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-center mr-1',
})<{logo_background: string}>`
  background-color: ${(props) => props.logo_background};
  height: 2rem;
  max-width: 6rem;
  padding: 0.5rem;
  overflow: hidden;
`;

const StyledBody = styled.div.attrs({className: 'py-2'})`
  background-color: #fafafa;
`;

const StyledContent = styled.div.attrs({
  className: 'shadow-sm text-center py-3',
})`
  background-color: #fff;
`;

const StyledSearch = styled.input.attrs({
  type: 'search',
  placeholder: 'Type your question here...',
})`
  font-size: 0.5rem;
  border: 1px solid ${(props) => props.color};
  border-top-left-radius: 30rem;
  border-bottom-left-radius: 30rem;
  padding: 0.5em 0.25em 0.5em 1.5em;
  transition: border-color, box-shadow;
  transition-duration: 0.4s;
  flex: 1 1 auto;
`;

const StyledSearchButton = styled.button<{background: string}>`
  margin: 0;
  outline: none;
  font-size: 0.5rem;
  padding: 0.5em 1em 0.5em 0.5em;
  background: ${(props) => props.background};
  color: #fff;
  border: 1px solid ${(props) => props.background};
  border-top-right-radius: 30rem;
  border-bottom-right-radius: 30rem;
`;

const StyledBread = styled.span<{color: string}>`
  font-size: 0.5rem;
  color: ${(props) => props.color};
`;

const StyledCategory = styled.div.attrs({
  className: 'd-inline-block mr-1 mt-2',
})<{
  color: string;
}>`
  border: 1px solid ${(props) => props.color};
  padding: 1rem;
  color: ${(props) => props.color};
  font-size: 0.5rem;
  width: 6rem;
`;
